import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { saveActiveJourneys, saveDoneJourneys } from '../../features/journeys';
import { useGetHistoryMutation } from '../../services/home.service';
import { EScheduleSteps, saveConfirmSchedule, updateScheduleSteps } from '../../features/schedule/schedule-slice';
import { resetCalendarsState } from '../../features/calendar/calendar-slice';
import JourneysModel from './model';

const PREVIEW_MAX = 2;

let historyLoaded = false;

export const resetHomeCache = () => {
  historyLoaded = false;
};

export default function JourneysViewModel() {
  const dispatch = useDispatch();
  const [getHistory, { isLoading }] = useGetHistoryMutation();
  const [expanded, setExpanded] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const {
    journeys: { activeJourneys: rawActiveJourneys, doneJourneys: rawDoneJourneys },
    schedule: {
      haveToAnswerAnamnese,
      confirm,
      ui: { scheduling }
    }
  } = useSelector((state: RootState) => state);

  const activeJourneys = useMemo(
    () => rawActiveJourneys.filter(journey => journey.journeyid !== ''),
    [rawActiveJourneys]
  );
  const doneJourneys = useMemo(() => rawDoneJourneys.filter(journey => journey.journeyid !== ''), [rawDoneJourneys]);
  const noActiveJourneys = activeJourneys.length <= 0;

  const loadJourneyHistory = () => {
    getHistory({})
      .unwrap()
      .then(data => {
        historyLoaded = true;
        const active = data.filter(JourneysModel.isActiveJourney);
        const done = data.filter(JourneysModel.isFinalizedJourney);
        dispatch(saveActiveJourneys(active));
        dispatch(saveDoneJourneys(done));
        dispatch(saveConfirmSchedule(false));
      })
      .catch(() => {
        historyLoaded = false;
      });
  };

  useEffect(() => {
    if (!historyLoaded) {
      loadJourneyHistory();
    }
    dispatch(resetCalendarsState());
    dispatch(updateScheduleSteps({ currentStep: EScheduleSteps.idle, previousStep: EScheduleSteps.idle }));
  }, []);

  useEffect(() => {
    if (haveToAnswerAnamnese || confirm) {
      loadJourneyHistory();
    }
  }, [haveToAnswerAnamnese, confirm]);

  const allJourneys = useMemo(() => [...activeJourneys, ...doneJourneys], [activeJourneys, doneJourneys]);
  const journeysWithYears = useMemo(() => JourneysModel.withScheduleYears(allJourneys), [allJourneys]);
  const years = useMemo(() => JourneysModel.extractYears(journeysWithYears), [journeysWithYears]);

  useEffect(() => {
    if (years.length === 0 || selectedYear !== null) return;
    const currentYear = new Date().getFullYear().toString();
    setSelectedYear(years.includes(currentYear) ? currentYear : years[years.length - 1]);
  }, [years, selectedYear]);

  const yearJourneys = useMemo(
    () =>
      selectedYear
        ? JourneysModel.sortByLastSchedule(JourneysModel.filterByYear(journeysWithYears, selectedYear))
        : [],
    [journeysWithYears, selectedYear]
  );

  const allYearItems = useMemo(() => JourneysModel.buildYearItems(yearJourneys), [yearJourneys]);

  const previewYearItems = useMemo(() => JourneysModel.getPreview(allYearItems, PREVIEW_MAX), [allYearItems]);

  const hasMore = !expanded && JourneysModel.hasMore(allYearItems.length, PREVIEW_MAX);
  const showMore = () => setExpanded(true);
  const selectYear = (year: string) => {
    setSelectedYear(year);
    setExpanded(false);
  };

  return {
    isLoading,
    noActiveJourneys,
    expanded,
    years,
    selectedYear,
    setSelectedYear: selectYear,
    yearItems: expanded ? allYearItems : previewYearItems,
    hasMore,
    showMore,
    scheduling
  };
}
